import { observable, computed, action, makeObservable } from 'mobx'

/**
 * Delegate for computing tabs.
 */
export type GetTabs<T> = () => Array<TabSpec<T> | false>

/**
 * Tab specification.
 */
export interface TabSpec<T> {
  title: string
  tab: T
  hasError: () => boolean
}

/**
 * Creates a tab spec.
 * @param tab
 * @param title
 * @param hasError
 * @constructor
 */
export function TabSpec<T>(
  tab: T,
  title: string,
  hasError?: () => boolean
): TabSpec<T> {
  return {
    tab,
    title,
    hasError: hasError ?? (() => false),
  }
}

/**
 * Tab controller.
 * `T` is the enum to use.
 */
export default class TabController<T> {
  /**
   * The internal ID.
   */
  @observable private currentTabId: T | null = null

  /**
   * Constructor.
   * @param getTabs
   */
  constructor(private getTabs: GetTabs<T>) {
    makeObservable(this)
  }

  /**
   * The currently selected tab.
   */
  @computed
  get current() {
    const tab = this.tabs.find((t) => t.tab === this.currentTabId)
    return tab || this.tabs[0]
  }

  /**
   * The available tabs.
   */
  @computed
  get tabs() {
    return this.getTabs().filter((x): x is TabSpec<T> => x !== false)
  }

  /**
   * Sets the tab.
   *
   * @param tabId
   */
  @action.bound
  change(tabId: T) {
    this.currentTabId = tabId
  }
}
